import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  CurecallConfirmationModal,
  CurecallTextarea,
  ECurecallNotificationAppearance,
} from '@honestica/curecall-ui'
import { useQueryClient } from '@tanstack/react-query'
import type { TFunction } from 'i18next'
import { useAuth } from 'src/hooks/use-auth'
import { useLocale } from 'src/hooks/use-locale'
import { useCurrentProject } from 'src/providers/current-project.provider'
import { useNotification } from 'src/providers/notification.provider'
import { HumanReviewRequestsService } from 'src/services/human-review-requests.service'
import { getTranslatedLabel } from 'src/utils/locale.util'
import { getFullName } from 'src/utils/string-formatter.util'
import {
  EHumanReviewRequestProblem,
  EHumanReviewRequestSubject,
  EHumanReviewResolvedBy,
  EHumanReviewResolution,
  type THumanReviewDetail,
  type THumanReviewRequest,
  type THumanReviewTab,
  getEffectiveResolution,
} from '../human-review-requests.type'
import { useProblemLabels } from '../use-problem-labels'
import { useSubjectLabels } from '../use-subject-labels'

export type TResolveStep = {
  subject: EHumanReviewRequestSubject
  requests: THumanReviewRequest[]
}

const ProblemListItem = ({ label, details }: { label: string; details: string[] }) => (
  <li>{details.length > 0 ? `${label} : ${details.join(', ')}` : label}</li>
)

const getConfirmButtonLabelByResolution = (t: TFunction): Record<EHumanReviewResolution, string> => ({
  [EHumanReviewResolution.PROCESSED]: t(
    'pages.dashboard.human-review-requests.problems.resolutions.mark-as-processed',
  ),
  [EHumanReviewResolution.UNREACHABLE]: t(
    'pages.dashboard.human-review-requests.problems.resolutions.mark-as-unreachable',
  ),
  [EHumanReviewResolution.CONTACTED]: t(
    'pages.dashboard.human-review-requests.problems.resolutions.mark-as-contacted',
  ),
})

const getSuccessNotificationByResolution = (t: TFunction): Record<EHumanReviewResolution, string> => ({
  [EHumanReviewResolution.PROCESSED]: t(
    'pages.dashboard.human-review-requests.problems.resolutions.success-notification',
  ),
  [EHumanReviewResolution.UNREACHABLE]: t(
    'pages.dashboard.human-review-requests.problems.resolutions.success-notification-unreachable',
  ),
  [EHumanReviewResolution.CONTACTED]: t(
    'pages.dashboard.human-review-requests.problems.resolutions.success-notification-contacted',
  ),
})

type Props = {
  steps: TResolveStep[]
  resolution: EHumanReviewResolution | null
  patientMedEventId: number
  patient: { firstName: string | null; lastName: string | null }
  onClose: () => void
}

const ResolveHrrModal = ({ steps, resolution, patientMedEventId, patient, onClose }: Props) => {
  const { t } = useTranslation()
  const { currentUser } = useAuth()
  const { locale } = useLocale()
  const { project } = useCurrentProject()
  const { notify } = useNotification()
  const queryClient = useQueryClient()
  const problemLabels = useProblemLabels()
  const subjectLabels = useSubjectLabels()
  const [stepIndex, setStepIndex] = useState(0)
  const [comment, setComment] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  useEffect(() => {
    if (resolution !== null) {
      setStepIndex(0)
      setComment('')
      setIsSubmitting(false)
    }
  }, [resolution])

  const clampedStepIndex = Math.min(stepIndex, steps.length - 1)
  const isOpen = resolution !== null
  const currentStep = steps.length > 0 ? steps[clampedStepIndex] : null
  const stepRequests = currentStep?.requests ?? []

  const smsNotReceivedRequests = stepRequests.filter(
    (request) => request.problem === EHumanReviewRequestProblem.SMS_NOT_RECEIVED,
  )
  const missingDocumentRequests = stepRequests.filter(
    (request) => request.problem === EHumanReviewRequestProblem.MISSING_DOCUMENT,
  )
  const declaredComplicationRequests = stepRequests.filter(
    (request) => request.problem === EHumanReviewRequestProblem.DECLARED_COMPLICATION,
  )
  const incompleteQuestionnaireRequests = stepRequests.filter(
    (request) => request.problem === EHumanReviewRequestProblem.INCOMPLETE_QUESTIONNAIRE,
  )
  const noResponseRequests = stepRequests.filter(
    (request) => request.problem === EHumanReviewRequestProblem.NO_RESPONSE,
  )
  const appointmentCancelledRequests = stepRequests.filter(
    (request) => request.problem === EHumanReviewRequestProblem.APPOINTMENT_CANCELLED,
  )
  const appointmentRescheduleRequests = stepRequests.filter(
    (request) => request.problem === EHumanReviewRequestProblem.APPOINTMENT_RESCHEDULE_REQUESTED,
  )
  const missingDischargeCompanionRequests = stepRequests.filter(
    (request) => request.problem === EHumanReviewRequestProblem.MISSING_DISCHARGE_COMPANION,
  )
  const effectiveResolution =
    currentStep && resolution ? getEffectiveResolution(currentStep.subject, resolution) : resolution

  const optimisticallyUpdateCache = (
    resolvedStep: TResolveStep,
    stepResolution: EHumanReviewResolution,
    isLastStep: boolean,
  ) => {
    queryClient.setQueryData<THumanReviewDetail>(
      ['human-review-requests-detail', project.id, patientMedEventId],
      (previous) => {
        if (!previous) return previous
        const resolvedIds = new Set(resolvedStep.requests.map((request) => request.id))
        return {
          ...previous,
          reviewRequests: previous.reviewRequests.map((request) =>
            resolvedIds.has(request.id)
              ? {
                  ...request,
                  resolution: stepResolution,
                  resolvedBy: EHumanReviewResolvedBy.USER,
                  resolvedByUserId: currentUser?.id ?? null,
                  resolvedAt: new Date().toISOString(),
                  resolutionComment: comment || null,
                }
              : request,
          ),
        }
      },
    )

    queryClient.setQueriesData<{ tabs: THumanReviewTab[] }>(
      { queryKey: ['human-review-requests-tabs', project.id] },
      (previous) => {
        if (!previous) return previous
        return {
          tabs: previous.tabs.map((tab) => {
            const isSubjectTab = tab.subject === (resolvedStep.subject as string)
            const isAllTab = tab.subject === 'ALL'
            if (!isSubjectTab && !isAllTab) return tab

            const problemCounts = { ...tab.problemCounts }
            const uniqueProblems = new Set(resolvedStep.requests.map((request) => request.problem))
            for (const problem of uniqueProblems) {
              const currentCount = problemCounts[problem] ?? 0
              if (currentCount > 0) {
                problemCounts[problem] = currentCount - 1
              }
            }

            const resolutionCounts = { ...tab.resolutionCounts }
            resolutionCounts[stepResolution] = (resolutionCounts[stepResolution] ?? 0) + 1

            const shouldDecrementCount = isSubjectTab || (isAllTab && isLastStep)

            return {
              ...tab,
              toProcessCount: shouldDecrementCount ? Math.max(0, tab.toProcessCount - 1) : tab.toProcessCount,
              problemCounts,
              resolutionCounts,
            }
          }),
        }
      },
    )
  }

  const buildTitle = () => {
    if (!currentStep) return ''
    if (currentStep.subject === EHumanReviewRequestSubject.UNKNOWN) {
      return t('pages.dashboard.human-review-requests.problems.resolutions.modal-title-unknown', {
        name: getFullName({
          firstname: patient.firstName ?? undefined,
          lastname: patient.lastName ?? undefined,
        }),
      })
    }
    const subjectLabel = subjectLabels[currentStep.subject] ?? currentStep.subject
    if (steps.length === 1) {
      return t('pages.dashboard.human-review-requests.problems.resolutions.modal-title-single', {
        subject: subjectLabel,
      })
    }
    return t('pages.dashboard.human-review-requests.problems.resolutions.modal-title', {
      subject: subjectLabel,
      current: clampedStepIndex + 1,
      total: steps.length,
    })
  }

  const handleConfirm = async () => {
    if (!currentStep || !effectiveResolution || isSubmitting) return
    setIsSubmitting(true)
    try {
      await HumanReviewRequestsService.resolveBySubject(project.id, {
        patientMedEventId,
        subject: currentStep.subject,
        resolution: effectiveResolution,
        resolutionComment: comment || undefined,
      })
    } catch {
      notify({
        title: t('common.actions.error'),
        message: t('pages.dashboard.human-review-requests.problems.resolutions.error-notification'),
        appearance: ECurecallNotificationAppearance.ERROR,
      })
      setIsSubmitting(false)
      return
    }
    const isLastStep = clampedStepIndex === steps.length - 1
    optimisticallyUpdateCache(currentStep, effectiveResolution, isLastStep)
    setComment('')
    setIsSubmitting(false)
    if (isLastStep) {
      onClose()
      notify({
        title: t('common.actions.success'),
        message: getSuccessNotificationByResolution(t)[effectiveResolution],
        appearance: ECurecallNotificationAppearance.SUCCESS,
      })
    } else {
      setStepIndex(clampedStepIndex + 1)
    }
  }

  const handleCancel = () => {
    onClose()
  }

  return (
    <CurecallConfirmationModal
      isOpen={isOpen}
      onConfirm={handleConfirm}
      onCancel={handleCancel}
      title={buildTitle()}
      cancelButtonLabel={t('common.actions.cancel')}
      confirmButtonLabel={
        effectiveResolution ? getConfirmButtonLabelByResolution(t)[effectiveResolution] : ''
      }
      wrapperClassName="c-w-[560px]"
    >
      <div className="c-flex c-flex-col c-gap-4">
        {currentStep && (
          <ul className="c-list-disc c-pl-5 c-text-sm c-text-grey-600">
            {smsNotReceivedRequests.length > 0 && (
              <ProblemListItem
                label={problemLabels[EHumanReviewRequestProblem.SMS_NOT_RECEIVED]}
                details={smsNotReceivedRequests
                  .filter((request) => request.SmsLog?.workflowIntlLabel || request.SmsLog?.conversationLabel)
                  .map((request) =>
                    request.SmsLog!.workflowIntlLabel
                      ? (getTranslatedLabel(locale, request.SmsLog!.workflowIntlLabel) ?? '')
                      : (request.SmsLog!.conversationLabel ?? ''),
                  )}
              />
            )}
            {missingDocumentRequests.length > 0 && (
              <ProblemListItem
                label={problemLabels[EHumanReviewRequestProblem.MISSING_DOCUMENT]}
                details={missingDocumentRequests
                  .filter((request) => request.Variable)
                  .map((request) => getTranslatedLabel(locale, request.Variable!.intlLabel) ?? '')
                  .filter(Boolean)}
              />
            )}
            {declaredComplicationRequests.length > 0 && (
              <ProblemListItem
                label={problemLabels[EHumanReviewRequestProblem.DECLARED_COMPLICATION]}
                details={declaredComplicationRequests
                  .filter((request) => request.PatientMedAlert)
                  .map((request) => request.PatientMedAlert!.MedAlert.label)}
              />
            )}
            {incompleteQuestionnaireRequests.length > 0 && (
              <ProblemListItem
                label={problemLabels[EHumanReviewRequestProblem.INCOMPLETE_QUESTIONNAIRE]}
                details={incompleteQuestionnaireRequests
                  .filter((request) => request.PatientConversation?.formLabel)
                  .map((request) => request.PatientConversation!.formLabel!)}
              />
            )}
            {noResponseRequests.length > 0 && (
              <ProblemListItem
                label={problemLabels[EHumanReviewRequestProblem.NO_RESPONSE]}
                details={noResponseRequests
                  .filter((request) => request.PatientConversation?.formLabel)
                  .map((request) => request.PatientConversation!.formLabel!)}
              />
            )}
            {appointmentCancelledRequests.length > 0 && (
              <ProblemListItem
                label={problemLabels[EHumanReviewRequestProblem.APPOINTMENT_CANCELLED]}
                details={appointmentCancelledRequests
                  .filter((request) => request.PatientConversation?.formLabel)
                  .map((request) => request.PatientConversation!.formLabel!)}
              />
            )}
            {appointmentRescheduleRequests.length > 0 && (
              <ProblemListItem
                label={problemLabels[EHumanReviewRequestProblem.APPOINTMENT_RESCHEDULE_REQUESTED]}
                details={appointmentRescheduleRequests
                  .filter((request) => request.PatientConversation?.formLabel)
                  .map((request) => request.PatientConversation!.formLabel!)}
              />
            )}
            {missingDischargeCompanionRequests.length > 0 && (
              <ProblemListItem
                label={problemLabels[EHumanReviewRequestProblem.MISSING_DISCHARGE_COMPANION]}
                details={missingDischargeCompanionRequests
                  .filter((request) => request.PatientConversation?.formLabel)
                  .map((request) => request.PatientConversation!.formLabel!)}
              />
            )}
          </ul>
        )}
        <CurecallTextarea
          name="resolution-comment"
          rows={4}
          label={t('pages.dashboard.human-review-requests.problems.resolutions.modal-comment-label')}
          placeholder={t(
            'pages.dashboard.human-review-requests.problems.resolutions.modal-comment-placeholder',
          )}
          value={comment}
          onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => setComment(event.target.value)}
        />
      </div>
    </CurecallConfirmationModal>
  )
}

export default ResolveHrrModal
